import { useState } from 'react';
import { Button, Modal, Table } from '@servicecompressionllc/sc-design-system';
import { formatDayTime } from '../../../lib/format.js';
import { displayName, PeoplePicker } from './PeoplePicker.js';
import type { ShareDetail, SharePerson } from './shareClient.js';
import type { ConversationShareControls } from './useSharingLogic.js';

interface ManageSharingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  controls: ConversationShareControls;
}

const statusOf = (r: ShareDetail['recipients'][number]) =>
  r.continued
    ? { text: 'Opened · continued it', className: 'text-success' }
    : r.opened
      ? { text: 'Opened', className: 'text-success' }
      : { text: 'Not opened yet', className: 'text-ink-subtle' };

const sinceLine = (count: number | undefined): string => {
  if (count === undefined || count === 0) return 'They see everything you’ve asked so far.';
  const asked = count === 1 ? '1 more question' : `${String(count)} more questions`;
  return `You’ve asked ${asked} since then. They only see what was shared.`;
};

/** Who has the conversation, what they've done with it, and the owner's controls. */
export const ManageSharingModal = ({
  open,
  onOpenChange,
  title,
  controls,
}: ManageSharingModalProps) => {
  const [adding, setAdding] = useState<SharePerson[]>([]);
  const { share, shareWith, updateToLatest, remove, stop } = controls;
  const detail = share.data ?? null;
  const failure = shareWith.error ?? updateToLatest.error ?? remove.error ?? stop.error;

  const close = () => {
    setAdding([]);
    onOpenChange(false);
  };
  const add = () =>
    shareWith.mutate({ userIds: adding.map((p) => p.userId) }, { onSuccess: () => setAdding([]) });
  const stopAll = () => stop.mutate(undefined, { onSuccess: close });

  return (
    <Modal open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <Modal.Header>
        <div className="flex flex-col gap-0.5">
          <Modal.Title>Sharing</Modal.Title>
          <Modal.Description>{title}</Modal.Description>
        </div>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="add-people"
            className="text-xs font-semibold uppercase tracking-wider text-ink-subtle"
          >
            Share with more people
          </label>
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <PeoplePicker
                id="add-people"
                selected={adding}
                onChange={setAdding}
                excludeIds={detail?.recipients.map((r) => r.userId)}
                placeholder="Name or email"
              />
            </div>
            <Button
              variant="secondary"
              onClick={add}
              disabled={adding.length === 0 || shareWith.isPending}
            >
              Add
            </Button>
          </div>
        </div>

        {detail && (
          <>
            <Table.Root className="w-full text-sm">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Person</Table.HeaderCell>
                  <Table.HeaderCell className="w-36">Shared</Table.HeaderCell>
                  <Table.HeaderCell className="w-24" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {detail.recipients.map((r) => {
                  const status = statusOf(r);
                  return (
                    <Table.Row key={r.userId}>
                      <Table.Cell>
                        <div className="flex flex-col">
                          <span className="text-ink">{displayName(r)}</span>
                          <span className={`text-xs ${status.className}`}>{status.text}</span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-ink-muted">{formatDayTime(r.addedAt)}</Table.Cell>
                      <Table.Cell>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() =>
                            remove.mutate(r.userId, {
                              // The last one out ends the share.
                              onSuccess: () => detail.recipients.length === 1 && close(),
                            })
                          }
                          disabled={remove.isPending}
                        >
                          Remove
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>

            <div className="flex flex-col gap-2 rounded border border-outline bg-surface px-3.5 py-3">
              <span className="text-sm font-medium text-ink">
                Shared as of {formatDayTime(detail.sharedUpTo)}
              </span>
              <span className="text-[13px] leading-normal text-ink-muted">
                {sinceLine(detail.questionsSince)}
              </span>
              <Button
                variant="secondary"
                className="self-start"
                onClick={() => updateToLatest.mutate()}
                disabled={updateToLatest.isPending || detail.questionsSince === 0}
              >
                Update to latest
              </Button>
            </div>
          </>
        )}

        <span className="text-xs text-ink-subtle">
          Removing someone takes it out of their Shared with me list. A copy they already continued
          stays theirs.
        </span>
        {failure && <p className="text-xs font-semibold text-danger">{failure.message}</p>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={stopAll} disabled={!detail || stop.isPending}>
          Stop sharing with everyone
        </Button>
        <div className="flex-1" />
        <Button onClick={close}>Done</Button>
      </Modal.Footer>
    </Modal>
  );
};
